import {
  AssetExecutionStatus,
  TableAssetFilters,
} from '@elementary/common-ui/services/gql/types';
import {
  FilterItem,
  Operator,
} from '@elementary/common-ui/hooks/useUrlFilters';
import { ModelRun, ModelRuns } from '@elementary/common-ui/types';
import { getValue } from '@elementary/common-ui/utils/common';
import { ModelRunsTableAssetItem } from './types';

type ModelRunStatus = ModelRun['status'];
type ResultsKey = keyof ModelRuns['totals'];

const MODEL_RUN_STATUS_TO_EXECUTION: Record<
  ModelRunStatus,
  AssetExecutionStatus
> = {
  success: 'SUCCESS',
  error: 'ERROR',
  skipped: 'SKIPPED',
};

const EXECUTION_STATUS_TO_MODEL_RUN: Record<
  AssetExecutionStatus,
  ModelRunStatus
> = {
  SUCCESS: 'success',
  ERROR: 'error',
  SKIPPED: 'skipped',
};

const isModelRunStatus = (value: string): value is ModelRunStatus =>
  value === 'success' || value === 'error' || value === 'skipped';

const isAssetExecutionStatus = (
  value: string,
): value is AssetExecutionStatus => {
  const upper = value.toUpperCase();
  return upper === 'SUCCESS' || upper === 'ERROR' || upper === 'SKIPPED';
};

const isResultsKey = (value: string): value is ResultsKey =>
  value === 'errors' || value === 'success';

const toExecutionStatuses = (values: string[]): AssetExecutionStatus[] =>
  values
    .filter(isModelRunStatus)
    .map((status) => MODEL_RUN_STATUS_TO_EXECUTION[status]);

export const toModelRunStatus = (status: string): ModelRunStatus => {
  const upper = status.toUpperCase();
  return isAssetExecutionStatus(upper)
    ? EXECUTION_STATUS_TO_MODEL_RUN[upper]
    : 'skipped';
};

const parseIsCriticalFilterValue = (value: string) =>
  value === 'Yes' || value === 'true';

export const mapUrlFiltersToTableAssetFilters = (
  filters: FilterItem[] | undefined,
): {
  tableAssetFilters: Partial<TableAssetFilters>;
  clientSideFilters: FilterItem[];
} => {
  const tableAssetFilters: Partial<TableAssetFilters> = {};
  const clientSideFilters: FilterItem[] = [];

  for (const filter of filters || []) {
    let mapped = false;

    if (filter.operator === 'equals') {
      switch (filter.property) {
        case 'database_name':
          tableAssetFilters.dbName = filter.value[0];
          mapped = true;
          break;
        case 'schema':
          tableAssetFilters.schemaName = filter.value[0];
          mapped = true;
          break;
        case 'name':
          tableAssetFilters.names = filter.value;
          mapped = true;
          break;
        case 'status':
          tableAssetFilters.statuses = toExecutionStatuses(filter.value);
          mapped = true;
          break;
        case 'asset_metadata.owners':
          tableAssetFilters.owners = filter.value;
          mapped = true;
          break;
        case 'asset_metadata.tags':
          tableAssetFilters.tags = filter.value;
          mapped = true;
          break;
        case 'asset_metadata.materialization':
          tableAssetFilters.buildStrategies = filter.value;
          mapped = true;
          break;
        case 'asset_metadata.subscribers':
          tableAssetFilters.subscribers = filter.value;
          mapped = true;
          break;
        case 'asset_metadata.is_critical':
          tableAssetFilters.isCritical = parseIsCriticalFilterValue(
            filter.value[0],
          );
          mapped = true;
          break;
        default:
          break;
      }
    }

    if (
      !mapped &&
      filter.operator === 'contains' &&
      filter.property === 'asset_metadata.path'
    ) {
      tableAssetFilters.path = filter.value[0];
      mapped = true;
    }

    if (!mapped) {
      clientSideFilters.push(filter);
    }
  }

  return { tableAssetFilters, clientSideFilters };
};

export const resultsColumnFilterMatcher = (
  selectedFilterValue: string[],
  rowData: ModelRuns,
  operator: Operator,
) => {
  const hasMatch = (predicate: (key: ResultsKey) => boolean) =>
    selectedFilterValue.every((val) => isResultsKey(val) && predicate(val));

  if (operator === 'equals') {
    return hasMatch((key) => rowData?.totals[key] > 0);
  }

  if (operator === 'notEquals') {
    return hasMatch((key) => rowData?.totals[key] === 0);
  }

  return false;
};

export const applyModelRunsClientSideFilters = (
  data: ModelRuns[],
  clientSideFilters: FilterItem[],
): ModelRuns[] => {
  if (!clientSideFilters.length) return data;

  return data.filter((item) =>
    clientSideFilters.every((filter) => {
      if (filter.property === 'totals') {
        return resultsColumnFilterMatcher(
          filter.value || [],
          item,
          filter.operator,
        );
      }

      const lowerCaseFilterValue = filter.value.map((val) =>
        val?.toString().toLowerCase(),
      );
      const tableCellValue = getValue(item, filter.property);
      const isValueArray = Array.isArray(tableCellValue);
      const lowerCaseCellValue = isValueArray
        ? tableCellValue.map((cellVal) => cellVal.toString().toLowerCase())
        : tableCellValue?.toString().toLowerCase() || '';

      if (isValueArray) {
        const includeEmpty = filter.value.some((i) => !i);

        switch (filter.operator) {
          case 'equals':
            return (
              lowerCaseCellValue?.some((cv: string) =>
                lowerCaseFilterValue.includes(cv),
              ) || (includeEmpty ? lowerCaseCellValue.length === 0 : false)
            );
          case 'contains':
            return lowerCaseCellValue?.some((cv: string) =>
              lowerCaseFilterValue.some((fv) => fv.includes(cv)),
            );
          case 'notEquals':
            return !(
              lowerCaseCellValue?.some((cv: string) =>
                lowerCaseFilterValue.includes(cv),
              ) || (includeEmpty ? lowerCaseCellValue.length === 0 : false)
            );
          case 'notContains':
            return !lowerCaseCellValue?.some((cv: string) =>
              lowerCaseFilterValue.some((fv) => cv.includes(fv)),
            );
          default:
            return false;
        }
      }

      switch (filter.operator) {
        case 'equals':
          return lowerCaseFilterValue
            ? lowerCaseFilterValue.includes(lowerCaseCellValue)
            : !lowerCaseCellValue;
        case 'contains':
          return lowerCaseFilterValue.some((filterValue) =>
            lowerCaseCellValue.includes(filterValue),
          );
        case 'notEquals':
          return lowerCaseFilterValue
            ? !lowerCaseFilterValue.includes(lowerCaseCellValue)
            : !!lowerCaseCellValue;
        case 'notContains':
          return lowerCaseFilterValue.every(
            (filterValue) => !lowerCaseCellValue.includes(filterValue),
          );
        default:
          return false;
      }
    }),
  );
};

export const parseTableAssetToModelRuns = (
  asset: ModelRunsTableAssetItem,
): ModelRuns => ({
  unique_id: asset.id,
  database_name: asset.dbName,
  schema: asset.schemaName,
  name: asset.name,
  status: toModelRunStatus(asset.latestExecution?.status || ''),
  last_exec_time: asset.latestExecution?.executionTime ?? 0,
  median_exec_time: asset.performanceStats?.medianDuration ?? 0,
  exec_time_change_rate: asset.performanceStats?.changeRate ?? 0,
  totals: {
    errors: asset.performanceStats?.errorCount ?? 0,
    success: asset.performanceStats?.successCount ?? 0,
  },
  runs: [],
  last_generated_at: '',
  compiled_code: null,
  asset_metadata: {
    owners: asset.owners,
    tags: asset.tags,
    materialization: asset.materialization,
    subscribers: asset.subscribers,
    path: asset.path ?? undefined,
    is_critical: asset.isCritical,
  },
});
